/**
 * "DISEASES with Category and Checkbox Options" — the pure logic behind the 5th Open Dental
 * data type (`control.dataType = 'DISEASES_CATEGORIZED'`).
 *
 * WHAT IT IS
 * The office groups Open Dental diseases into named categories ("Heart or Circulatory",
 * "Lung or Breathing", …). The patient taps a category tile and ticks checkboxes instead of
 * typing into a `vue-multiselect`. Everything here is config + UI state shaping; the write-back
 * to Open Dental is the EXISTING Diseases path and is not touched.
 *
 * THE STORED-SHAPE CONTRACT (do not break it — the backend depends on it)
 *   value === `[{ text, value, category? }]`, `value` being an OD `DiseaseDefNum`.
 *   - `category` is the category NAME the item was ticked under. It is an ADDITIVE tag that
 *     `forms-service/forms-api.js saveFormSubmissionDataToOD` ignores; it exists so the staff
 *     submission view and the PDF can group the items. Nothing may depend on it being present.
 *   - Items are deduped by `String(value)`. A duplicate DiseaseDefNum would double-INSERT into
 *     Open Dental's `disease` table.
 *   - "No conditions" stores the None sentinel `[{ text: 'None', value: 'none' }]` — exactly what
 *     picking None in the legacy dropdown stores — so the backend resolves every active problem.
 *   - Charted problems the office never put in a category are PRESERVED in the value untouched,
 *     so a form can never resolve a problem the patient was never shown.
 *   - Open Dental's own "Other" problem carries the patient's description as
 *     `otherTypeDescriptionText` (the key forms-service reads into `disease.PatNote`). The submit
 *     path ALSO sends that text as `odDataToSave['Other Problems']`: the list diff only writes the
 *     note when it INSERTs or re-activates "Other"; an already-active "Other" gets its note from
 *     Other Problems. (forms-service also resolved "Other" whenever Other Problems arrived blank
 *     until 2026-09-18.) See getOtherNote.
 *
 * HOW THE PATIENT ANSWERS "OTHER" (2026-09-18, Nilay) — "Other" is never a checkbox:
 *   - A category holding ONLY "Other" (the default "Other Conditions") IS the answer: tapping its
 *     tile selects "Other" and shows just the description box, which is REQUIRED.
 *   - A category holding "Other" among other conditions shows those conditions as checkboxes plus
 *     an OPTIONAL description box: "Other" is selected exactly when the box has text.
 *   See syncOtherNote (the rules), isOtherOnlyCategory and isOtherNoteRequired.
 *
 * Every function here is pure: inputs are never mutated and fresh objects/arrays are returned.
 * Malformed config or values never throw — the builder and the patient form must degrade quietly.
 *
 * Import-free on purpose: cypress/node-tests/forms-od-categorized-diseases.test.js executes this
 * file (it strips the single trailing export block and evaluates the rest with `new Function`), so
 * the tests verify production code rather than a copy. Do not add an `import` here.
 */

/** Internal `control.dataType` key for the categorized diseases field. */
const CATEGORIZED_DATA_TYPE = 'DISEASES_CATEGORIZED';

/** What the builder's "Select Open Dental Data Type" dropdown shows for that key. */
const CATEGORIZED_DATA_TYPE_LABEL = 'DISEASES with Category and Checkbox Options';

/** Category names every site starts from (empty disease lists — the office assigns them). */
const DEFAULT_DISEASE_CATEGORIES = [
    'Heart or Circulatory',
    'Lung or Breathing',
    'Digestive or Dietary',
    'Neurological',
    'Autoimmune',
    'Other Conditions'
];

/** Default caption of the exclusive "nothing applies" tile. */
const DEFAULT_NONE_LABEL = 'No conditions';

/** Heading of the display group holding charted problems that are in no category. */
const ALSO_ON_FILE_LABEL = 'Also on file (not shown to patient)';

/** Synthetic group id used by groupForDisplay for the None sentinel. */
const NONE_GROUP_ID = '__none__';

/** Synthetic group id used by groupForDisplay for preserved (uncategorized) problems. */
const PRESERVED_GROUP_ID = '__preserved__';

/** Save-gate message: this data type writes DiseaseDefNums, so only Diseases (or nothing) fits. */
const CATEGORIZED_MAPPING_ERROR = 'A DISEASES with Category and Checkbox Options field can only be saved to the Diseases Open Dental field (or left unmapped).';

/**
 * Open Dental's own "Other" problem. forms-service finds it by exactly this name (case-insensitive)
 * and writes Other Problems into ITS note, so this is the one checkbox that gets a description box.
 */
const OTHER_DISEASE_NAME = 'Other';

/** The item key forms-service reads as the "Other" problem's note (`disease.PatNote`). */
const OTHER_NOTE_KEY = 'otherTypeDescriptionText';

/** The default category Open Dental's "Other" is placed in when the control is set up. */
const DEFAULT_OTHER_CATEGORY = 'Other Conditions';

/** UI cap on the "Other" description. */
const OTHER_NOTE_MAX_LENGTH = 1000;

/**
 * Categories the AI auto-assign (POST /categorize-diseases) may never target: "Other Conditions"
 * is the office's catch-all and the None tile is the opposite of a condition, so nothing is ever
 * put in them automatically. ⛔ This regex is DUPLICATED VERBATIM in forms-service
 * (lib/categorizeDiseases.js) — it is both what the prompt is allowed to offer and what the
 * result is filtered against. Both repos pin its source string in a test; change neither alone.
 */
const EXCLUDED_CATEGORY_RX = /^\s*(other|no\s+conditions?|none)\b/i;

/**
 * "Is this submitted item the None sentinel?"
 * MIRRORS libraries/alert-highlight.js and forms-service isNoneOption — sync-tested.
 *
 * @param {{text?: string, value?: *}} item
 * @returns {boolean}
 */
function isNoneOption(item) {
    if (!item) return false;
    const textLower = (item.text || '').toLowerCase();
    const valueLower = String(item.value || '').toLowerCase();
    return textLower === 'none' || valueLower === 'none';
}

/**
 * True only for a plain (non-array) object.
 *
 * @param {*} candidate
 * @returns {boolean}
 */
function isPlainObject(candidate) {
    return !!candidate && typeof candidate === 'object' && !Array.isArray(candidate);
}

/**
 * `String(x)` for comparisons, with null/undefined collapsing to ''.
 *
 * @param {*} raw
 * @returns {string}
 */
function asText(raw) {
    return raw === null || raw === undefined ? '' : String(raw);
}

/**
 * Trimmed, upper-cased data type key (tolerates null/undefined/whitespace).
 *
 * @param {*} dataType
 * @returns {string}
 */
function normalizeDataType(dataType) {
    return asText(dataType).trim().toUpperCase();
}

/**
 * Is this control an Open Dental Data Field set to the categorized diseases type?
 *
 * @param {Object} control
 * @returns {boolean}
 */
function isCategorizedDiseases(control) {
    if (!isPlainObject(control)) return false;
    if (control.type !== 'opendentaldata') return false;
    return normalizeDataType(control.dataType) === CATEGORIZED_DATA_TYPE;
}

/**
 * The dataType to send to `/get-od-data`. The endpoint 400s on an unknown type, so the
 * categorized key is translated to the plain DISEASES fetch; everything else passes through.
 *
 * @param {*} dataType
 * @returns {*}
 */
function requestDataTypeFor(dataType) {
    return normalizeDataType(dataType) === CATEGORIZED_DATA_TYPE ? 'DISEASES' : dataType;
}

/**
 * Human label for a data type key (only the categorized key has one; the rest show as-is).
 *
 * @param {*} dataType
 * @returns {*}
 */
function dataTypeLabel(dataType) {
    return normalizeDataType(dataType) === CATEGORIZED_DATA_TYPE ? CATEGORIZED_DATA_TYPE_LABEL : dataType;
}

/**
 * A fresh None sentinel item — the exact shape the backend strips before writing to Open Dental.
 *
 * @returns {{text: string, value: string}}
 */
function makeNoneItem() {
    return { text: 'None', value: 'none' };
}

/**
 * Is this option/item Open Dental's "Other" problem? Same rule as forms-service (`DiseaseName =
 * "Other"`) and the legacy ViewForm pairing (`text.toUpperCase() === 'OTHER'`), trimmed.
 *
 * @param {*} item
 * @returns {boolean}
 */
function isOtherOption(item) {
    if (!isPlainObject(item)) return false;
    return asText(item.text).trim().toLowerCase() === OTHER_DISEASE_NAME.toLowerCase();
}

/**
 * Does this category hold Open Dental's "Other" and nothing else (the default "Other Conditions")?
 * Its tile then IS the answer "Other": tapping it selects "Other" and shows only the required
 * description box. A category with "Other" among other conditions is NOT one of these.
 *
 * @param {*} category
 * @returns {boolean}
 */
function isOtherOnlyCategory(category) {
    if (!isPlainObject(category) || !Array.isArray(category.options)) return false;
    const options = category.options.filter(isPlainObject);
    return options.length > 0 && options.every(isOtherOption);
}

/**
 * The trimmed "Other" description carried on an item, or ''.
 *
 * @param {*} item
 * @returns {string}
 */
function itemOtherNote(item) {
    if (!isPlainObject(item)) return '';
    const note = item[OTHER_NOTE_KEY];
    if (typeof note !== 'string') return '';
    const trimmed = note.trim();
    // The legacy path guards the literal 'undefined' the same way (a stringified missing value).
    return trimmed.toLowerCase() === 'undefined' ? '' : trimmed;
}

/**
 * A new `cat_<8 lowercase hex>` category id that is not already taken.
 *
 * @param {Array<string>} [existingIds]
 * @returns {string}
 */
function newCategoryId(existingIds = []) {
    const taken = (Array.isArray(existingIds) ? existingIds : []).map(asText);
    let id = '';
    do {
        let hex = '';
        for (let i = 0; i < 8; i++) {
            hex += Math.floor(Math.random() * 16).toString(16);
        }
        id = 'cat_' + hex;
    } while (taken.indexOf(id) !== -1);
    return id;
}

/**
 * Copy an option list down to `{text, value}` entries with a usable value.
 *
 * @param {*} options
 * @returns {Array<{text: string, value: *}>}
 */
function normalizeOptions(options) {
    if (!Array.isArray(options)) return [];
    return options
        .filter(opt => isPlainObject(opt) && opt.value !== null && opt.value !== undefined)
        .map(opt => ({ text: asText(opt.text), value: opt.value }));
}

/**
 * Defensive copy of a `control.categories` array: every row gets a unique id, a trimmed string
 * name (blank names are KEPT — the office may still be typing) and a clean option list. Junk rows
 * are dropped; extra per-row properties (e.g. `nameEs`) are carried over untouched.
 *
 * @param {*} categories
 * @returns {Array<{id: string, name: string, options: Array<{text: string, value: *}>}>}
 */
function normalizeCategories(categories) {
    if (!Array.isArray(categories)) return [];
    const rows = categories.filter(isPlainObject);
    const taken = rows.map(row => asText(row.id)).filter(id => id.trim() !== '');
    return rows.map(row => {
        let id = asText(row.id).trim();
        if (id === '') {
            id = newCategoryId(taken);
            taken.push(id);
        }
        return Object.assign({}, row, {
            id,
            name: asText(row.name).trim(),
            options: normalizeOptions(row.options)
        });
    });
}

/**
 * Categories of a control, normalized. An absent/garbage `categories` is an empty list.
 *
 * @param {Object} control
 * @returns {Array<Object>}
 */
function controlCategories(control) {
    return normalizeCategories(isPlainObject(control) ? control.categories : []);
}

/**
 * Put Open Dental's "Other" problem into the "Other Conditions" category, so a new control asks
 * patients to describe "Other" with no setup. Does nothing when the site has no "Other" problem,
 * when "Other" already sits in ANY category (the office may have moved it on purpose), or when no
 * category is called "Other Conditions" (compared trimmed, case-insensitively).
 *
 * @param {*} categories
 * @param {*} allOptions - the site's full OD disease list ({text, value} items)
 * @returns {Array<Object>} normalized categories (fresh objects; the input is never mutated)
 */
function placeOtherInDefaultCategory(categories, allOptions) {
    const normalized = normalizeCategories(categories);
    const other = (Array.isArray(allOptions) ? allOptions : [])
        .filter(opt => isPlainObject(opt) && opt.value !== null && opt.value !== undefined)
        .filter(isOtherOption)[0];
    if (!other) return normalized;

    if (categoryIdsForValue(normalized, other.value).length > 0) return normalized;

    const target = normalized.filter(
        cat => cat.name.trim().toLowerCase() === DEFAULT_OTHER_CATEGORY.toLowerCase()
    )[0];
    if (!target) return normalized;

    target.options.push({ text: asText(other.text), value: other.value });
    return normalized;
}

/**
 * The six shipped categories, with fresh ids. Only "Other Conditions" can start non-empty: it gets
 * Open Dental's "Other" problem when the site has one (placeOtherInDefaultCategory). The office
 * assigns everything else.
 *
 * @param {*} [allOptions] - the site's full OD disease list; absent/empty ⇒ all six start empty
 * @returns {Array<{id: string, name: string, options: Array}>}
 */
function seedDefaultCategories(allOptions = []) {
    const seeded = [];
    DEFAULT_DISEASE_CATEGORIES.forEach(name => {
        seeded.push({ id: newCategoryId(seeded.map(cat => cat.id)), name, options: [] });
    });
    return placeOtherInDefaultCategory(seeded, allOptions);
}

/**
 * "Restore default categories": append every default whose name is missing (compared trimmed and
 * case-insensitively). Existing rows — renamed, reordered or extra — are never removed or moved.
 * Open Dental's "Other" is then placed in "Other Conditions" if it is in no category.
 *
 * @param {*} existing
 * @param {*} [allOptions] - the site's full OD disease list
 * @returns {Array<Object>}
 */
function restoreDefaultCategories(existing, allOptions = []) {
    const restored = normalizeCategories(existing);
    const present = restored.map(cat => cat.name.trim().toLowerCase());
    DEFAULT_DISEASE_CATEGORIES.forEach(name => {
        if (present.indexOf(name.trim().toLowerCase()) !== -1) return;
        restored.push({ id: newCategoryId(restored.map(cat => cat.id)), name, options: [] });
        present.push(name.trim().toLowerCase());
    });
    return placeOtherInDefaultCategory(restored, allOptions);
}

/**
 * The deduped union of every category's options, in category order then option order. This is what
 * `control.selectedOptions` is kept equal to, so the alert picker and ViewForm's "Other" lookup
 * keep working unchanged.
 *
 * @param {*} categories
 * @returns {Array<{text: string, value: *}>}
 */
function unionCategoryOptions(categories) {
    const seen = [];
    const union = [];
    normalizeCategories(categories).forEach(cat => {
        cat.options.forEach(opt => {
            const key = String(opt.value);
            if (seen.indexOf(key) !== -1) return;
            seen.push(key);
            union.push({ text: opt.text, value: opt.value });
        });
    });
    return union;
}

/**
 * Every offered disease that is not in any category yet — the builder's "Unassigned" helper.
 *
 * @param {*} allOptions - the full OD list ({text, value} items)
 * @param {*} categories
 * @returns {Array<Object>} shallow copies of the unassigned entries, in their original order
 */
function unassignedOptions(allOptions, categories) {
    if (!Array.isArray(allOptions)) return [];
    const assigned = unionCategoryOptions(categories).map(opt => String(opt.value));
    return allOptions
        .filter(opt => isPlainObject(opt) && opt.value !== null && opt.value !== undefined)
        .filter(opt => assigned.indexOf(String(opt.value)) === -1)
        .map(opt => Object.assign({}, opt));
}

/**
 * Is this category name one the auto-assign must never target? See EXCLUDED_CATEGORY_RX.
 *
 * @param {*} name
 * @returns {boolean}
 */
function isExcludedCategoryName(name) {
    return EXCLUDED_CATEGORY_RX.test(asText(name));
}

/**
 * The categories the AI may be offered as targets: everything except the excluded names and rows
 * the office has not named yet (a blank name tells the model nothing). Returned as the exact
 * `[{id, name}]` shape POST /categorize-diseases takes.
 *
 * @param {*} categories
 * @returns {Array<{id: string, name: string}>}
 */
function assignableCategories(categories) {
    return normalizeCategories(categories)
        .filter(cat => cat.name !== '' && !isExcludedCategoryName(cat.name))
        .map(cat => ({ id: cat.id, name: cat.name }));
}

/**
 * Apply the endpoint's `assignments` to a category list. Nothing is ever removed or moved:
 * each accepted `{value, categoryId}` APPENDS `{text, value}` (taken from the site's own option
 * list, so the model's text can never reach the config) to that category. An assignment is
 * dropped when the value is already in ANY category, when it is not an offered disease, when the
 * category id is unknown, or when the target is an excluded category — the endpoint filters those
 * too, this is the belt-and-braces copy.
 *
 * The input is never mutated: a fresh (normalized) category array comes back.
 *
 * @param {*} categories
 * @param {*} assignments - `[{value, categoryId}]`
 * @param {*} allOptions - the site's full OD disease list ({text, value} items)
 * @returns {{categories: Array<Object>, assigned: number, byCategory: Object}}
 */
function applyAssignments(categories, assignments, allOptions) {
    const next = normalizeCategories(categories);

    const targets = {};
    next.forEach(cat => {
        if (cat.name !== '' && !isExcludedCategoryName(cat.name)) targets[cat.id] = cat;
    });

    const offered = {};
    (Array.isArray(allOptions) ? allOptions : []).forEach(opt => {
        if (!isPlainObject(opt) || opt.value === null || opt.value === undefined) return;
        const key = String(opt.value);
        if (!Object.prototype.hasOwnProperty.call(offered, key)) offered[key] = opt;
    });

    // Every value already spoken for, growing as assignments land (so a duplicated value in the
    // response is only ever honoured once).
    const taken = unionCategoryOptions(next).map(opt => String(opt.value));
    const byCategory = {};
    let assigned = 0;

    (Array.isArray(assignments) ? assignments : []).forEach(row => {
        if (!isPlainObject(row) || row.value === null || row.value === undefined) return;
        const key = String(row.value);
        if (taken.indexOf(key) !== -1) return;
        const option = offered[key];
        if (!option) return;
        const target = targets[asText(row.categoryId).trim()];
        if (!target) return;

        target.options.push({ text: asText(option.text), value: option.value });
        taken.push(key);
        assigned++;
        byCategory[target.name] = (byCategory[target.name] || 0) + 1;
    });

    return { categories: next, assigned, byCategory };
}

/**
 * Ids of the already-normalized categories that contain a value (String()-compared, because
 * DefNums arrive as numbers from Open Dental and as strings from a saved submission).
 *
 * @param {Array<Object>} normalized
 * @param {*} value
 * @returns {Array<string>}
 */
function categoryIdsForValue(normalized, value) {
    const key = String(value);
    return normalized
        .filter(cat => cat.options.some(opt => String(opt.value) === key))
        .map(cat => cat.id);
}

/**
 * Ids of the categories that contain a value, in category order.
 *
 * @param {*} categories
 * @param {*} value
 * @returns {Array<string>}
 */
function findCategoryIdsForValue(categories, value) {
    return categoryIdsForValue(normalizeCategories(categories), value);
}

/**
 * Which category does a stored item belong to? Its `category` NAME tag wins when it names one of
 * the categories that actually contains the value; otherwise the first containing category; '' when
 * no category contains it (the item is then preserved).
 *
 * @param {Array<Object>} normalized
 * @param {Object} item
 * @returns {string} category id, or ''
 */
function categoryIdForItem(normalized, item) {
    const candidates = categoryIdsForValue(normalized, item.value);
    if (candidates.length === 0) return '';
    const tag = asText(item.category).trim().toLowerCase();
    if (tag !== '') {
        const tagged = normalized.filter(
            cat => candidates.indexOf(cat.id) !== -1 && cat.name.trim().toLowerCase() === tag
        );
        if (tagged.length > 0) return tagged[0].id;
    }
    return candidates[0];
}

/**
 * The blank patient-side UI state. `ticked` maps a category id to an array of `String(value)`;
 * `otherNote` is the raw (untrimmed, as typed) description of a ticked "Other".
 *
 * @returns {{noConditions: boolean, ticked: Object, openCategoryIds: Array<string>, preserved: Array, otherNote: string}}
 */
function emptyState() {
    return { noConditions: false, ticked: {}, openCategoryIds: [], preserved: [], otherNote: '' };
}

/**
 * Defensive copy of a UI state (never mutates the one handed in).
 *
 * @param {*} state
 * @returns {Object}
 */
function cloneState(state) {
    const source = isPlainObject(state) ? state : {};
    const sourceTicked = isPlainObject(source.ticked) ? source.ticked : {};
    const ticked = {};
    Object.keys(sourceTicked).forEach(categoryId => {
        ticked[categoryId] = Array.isArray(sourceTicked[categoryId])
            ? sourceTicked[categoryId].map(String)
            : [];
    });
    return {
        noConditions: source.noConditions === true,
        ticked,
        openCategoryIds: Array.isArray(source.openCategoryIds) ? source.openCategoryIds.slice() : [],
        preserved: Array.isArray(source.preserved) ? source.preserved.slice() : [],
        otherNote: asText(source.otherNote)
    };
}

/**
 * Derive the patient-side UI state from a stored value (prefilled chart data or a resumed form).
 * A lone None (or several) means "No conditions"; a value that is in no category is carried in
 * `preserved` so it survives the next submit untouched.
 *
 * @param {Object} control
 * @param {*} value - expected `[{text, value, category?}]`
 * @returns {Object} UI state
 */
function initStateFromValue(control, value) {
    if (!Array.isArray(value)) return emptyState();

    const normalized = controlCategories(control);
    const items = value.filter(isPlainObject);
    const real = items.filter(item => !isNoneOption(item));

    if (items.length > 0 && real.length === 0) {
        return Object.assign(emptyState(), { noConditions: true });
    }

    const ticked = {};
    const preserved = [];
    const preservedKeys = [];
    let otherNote = '';

    real.forEach(item => {
        const key = String(item.value);
        const categoryId = categoryIdForItem(normalized, item);
        // A charted "Other" with NO description, in a category that also lists other conditions, is
        // carried untouched too: there "Other" is only selected by typing in the OPTIONAL box, so
        // showing it unselected would silently set it inactive on the next submit. (In an
        // "Other"-only category it is shown selected instead, and the required box asks for a note.)
        const blankOtherInMixed = categoryId !== '' && isOtherOption(item) && itemOtherNote(item) === ''
            && !isOtherOnlyCategory(normalized.filter(cat => cat.id === categoryId)[0]);
        if (categoryId === '' || blankOtherInMixed) {
            // A preserved "Other" keeps its note ON the item (re-sent untouched on submit).
            if (preservedKeys.indexOf(key) !== -1) return;
            preservedKeys.push(key);
            preserved.push(Object.assign({}, item));
            return;
        }
        if (!Array.isArray(ticked[categoryId])) ticked[categoryId] = [];
        if (ticked[categoryId].indexOf(key) === -1) ticked[categoryId].push(key);
        if (otherNote === '' && isOtherOption(item)) otherNote = itemOtherNote(item);
    });

    const openCategoryIds = normalized
        .filter(cat => Array.isArray(ticked[cat.id]) && ticked[cat.id].length > 0)
        .map(cat => cat.id);

    return { noConditions: false, ticked, openCategoryIds, preserved, otherNote };
}

/**
 * Tap a category tile. Opening just reveals its checkboxes; CLOSING drops that category's ticks
 * (the patient un-answered it). Either way "No conditions" is cleared.
 *
 * @param {Object} state
 * @param {string} categoryId
 * @returns {Object} a new state
 */
function toggleCategoryOpen(state, categoryId) {
    const next = cloneState(state);
    const index = next.openCategoryIds.indexOf(categoryId);
    if (index === -1) {
        next.openCategoryIds.push(categoryId);
    } else {
        next.openCategoryIds.splice(index, 1);
        delete next.ticked[categoryId];
    }
    next.noConditions = false;
    return next;
}

/**
 * Tick or untick one checkbox. The category is opened if it was not already, and "No conditions"
 * is cleared.
 *
 * @param {Object} state
 * @param {string} categoryId
 * @param {*} value
 * @param {boolean} on
 * @returns {Object} a new state
 */
function setTick(state, categoryId, value, on) {
    const next = cloneState(state);
    if (next.openCategoryIds.indexOf(categoryId) === -1) {
        next.openCategoryIds.push(categoryId);
    }
    if (!Array.isArray(next.ticked[categoryId])) {
        next.ticked[categoryId] = [];
    }
    const key = String(value);
    const index = next.ticked[categoryId].indexOf(key);
    if (on) {
        if (index === -1) next.ticked[categoryId].push(key);
    } else if (index !== -1) {
        next.ticked[categoryId].splice(index, 1);
    }
    next.noConditions = false;
    return next;
}

/**
 * Tap "No conditions": everything else is cleared, INCLUDING preserved problems — answering "no
 * conditions" is a deliberate statement that nothing on the chart is active any more.
 *
 * @param {Object} state - ignored; the result is absolute
 * @returns {Object} a new state
 */
function setNoConditions(state) {
    return { noConditions: true, ticked: {}, openCategoryIds: [], preserved: [], otherNote: '' };
}

/**
 * Type into the "Other" description box. Stored raw (as typed); projectState trims it.
 *
 * @param {Object} state
 * @param {*} note
 * @returns {Object} a new state
 */
function setOtherNote(state, note) {
    const next = cloneState(state);
    next.otherNote = asText(note);
    return next;
}

/**
 * Id of the category the "Other" description box shows under: the FIRST open category (config
 * order) that holds Open Dental's "Other" — ticked or not, since "Other" is never a checkbox — or ''
 * when no such category is open.
 *
 * @param {Object} control
 * @param {Object} state
 * @returns {string}
 */
function otherNoteCategoryId(control, state) {
    const next = cloneState(state);
    if (next.noConditions) return '';
    const hit = controlCategories(control).filter(cat =>
        next.openCategoryIds.indexOf(cat.id) !== -1 && cat.options.some(isOtherOption)
    )[0];
    return hit ? hit.id : '';
}

/**
 * Is Open Dental's "Other" ticked in an open category (i.e. will it be stored)?
 *
 * @param {Object} control
 * @param {Object} state
 * @returns {boolean}
 */
function isOtherTicked(control, state) {
    const next = cloneState(state);
    if (next.noConditions) return false;
    return controlCategories(control).some(cat => {
        if (next.openCategoryIds.indexOf(cat.id) === -1) return false;
        const ticks = Array.isArray(next.ticked[cat.id]) ? next.ticked[cat.id] : [];
        return cat.options.some(opt => isOtherOption(opt) && ticks.indexOf(String(opt.value)) !== -1);
    });
}

/**
 * Is the description box showing AND required? Only under a category holding nothing but "Other"
 * (its tile IS the answer "Other"); among other conditions the box is optional.
 *
 * @param {Object} control
 * @param {Object} state
 * @returns {boolean}
 */
function isOtherNoteRequired(control, state) {
    const categoryId = otherNoteCategoryId(control, state);
    if (categoryId === '') return false;
    return isOtherOnlyCategory(controlCategories(control).filter(cat => cat.id === categoryId)[0]);
}

/**
 * Keep "Other" in step with its description box — run after EVERY state change. "Other" is never
 * a checkbox, so this is the only place it gets ticked:
 *   - under the box's category (otherNoteCategoryId): an "Other"-only category is itself the answer,
 *     so "Other" is ticked while it is open; a category with other conditions ticks "Other" exactly
 *     when the box has text;
 *   - in any other category "Other" is unticked (it is only ever answered through the box);
 *   - with no box showing (its category closed, "No conditions"), what was typed is DISCARDED, so
 *     text the patient can no longer see is never saved.
 *
 * @param {Object} control
 * @param {Object} state
 * @returns {Object} a new state
 */
function syncOtherNote(control, state) {
    const next = cloneState(state);
    const boxCategoryId = otherNoteCategoryId(control, next);
    if (boxCategoryId === '') next.otherNote = '';
    const hasText = next.otherNote.trim() !== '';

    controlCategories(control).forEach(cat => {
        const otherKeys = cat.options.filter(isOtherOption).map(opt => String(opt.value));
        if (otherKeys.length === 0) return;
        const current = Array.isArray(next.ticked[cat.id]) ? next.ticked[cat.id] : null;
        const ticks = (current || []).filter(key => otherKeys.indexOf(key) === -1);
        const on = cat.id === boxCategoryId && (isOtherOnlyCategory(cat) || hasText);
        if (on) ticks.push(otherKeys[0]);
        if (current || ticks.length > 0) next.ticked[cat.id] = ticks;
    });
    return next;
}

/**
 * The value to store for a UI state — see the stored-shape contract at the top of this file.
 * Ticks on a CLOSED category are ignored, duplicates across categories collapse to one item, and
 * preserved problems are appended untouched. A ticked "Other" carries the trimmed description as
 * `otherTypeDescriptionText` (key absent when blank).
 *
 * @param {Object} control
 * @param {Object} state
 * @returns {Array<{text: string, value: *, category?: string, otherTypeDescriptionText?: string}>}
 */
function projectState(control, state) {
    const next = cloneState(state);
    if (next.noConditions) return [makeNoneItem()];

    const normalized = controlCategories(control);
    const emitted = [];
    const projected = [];
    const note = next.otherNote.trim();

    normalized.forEach(cat => {
        if (next.openCategoryIds.indexOf(cat.id) === -1) return;
        const ticks = Array.isArray(next.ticked[cat.id]) ? next.ticked[cat.id] : [];
        cat.options.forEach(opt => {
            const key = String(opt.value);
            if (ticks.indexOf(key) === -1) return;
            if (emitted.indexOf(key) !== -1) return;
            emitted.push(key);
            const item = { text: opt.text, value: opt.value, category: cat.name };
            if (note !== '' && isOtherOption(opt)) item[OTHER_NOTE_KEY] = note;
            projected.push(item);
        });
    });

    next.preserved.forEach(item => {
        if (!isPlainObject(item)) return;
        const key = String(item.value);
        if (emitted.indexOf(key) !== -1) return;
        emitted.push(key);
        projected.push(Object.assign({}, item));
    });

    return projected;
}

/**
 * The "Other" description in a stored value — what the submit path sends as
 * `odDataToSave['Other Problems']` — or '' when there is none. Reads the first "Other" item with a
 * note, ticked OR preserved: a preserved "Other" re-sends its charted note unchanged. (Before the
 * 2026-09-18 forms-service fix, that re-send was also what kept a blank Other Problems from
 * resolving it; forms-service no longer resolves "Other" on a blank.)
 *
 * @param {*} value - the stored `[{text, value, …}]`
 * @returns {string}
 */
function getOtherNote(value) {
    if (!Array.isArray(value)) return '';
    const withNote = value
        .filter(item => !isNoneOption(item) && isOtherOption(item))
        .map(itemOtherNote)
        .filter(note => note !== '');
    return withNote.length > 0 ? withNote[0] : '';
}

/**
 * The "Other" description is REQUIRED when the patient picked a category holding nothing but
 * "Other" (its tile IS the answer): true when the value holds an "Other" with no description that
 * belongs to such a category. Among other conditions the box is optional — "Other" is only stored
 * there once text is typed, and a charted blank "Other" is carried untouched — so it never blocks.
 * A preserved "Other" (in no category, never shown) never blocks either.
 *
 * @param {Object} control
 * @param {*} value
 * @returns {boolean}
 */
function needsOtherNote(control, value) {
    if (!isCategorizedDiseases(control) || !Array.isArray(value)) return false;
    const normalized = controlCategories(control);
    return value
        .filter(item => isPlainObject(item) && !isNoneOption(item) && isOtherOption(item))
        .some(item => {
            if (itemOtherNote(item) !== '') return false;
            const categoryId = categoryIdForItem(normalized, item);
            return categoryId !== '' && isOtherOnlyCategory(normalized.filter(cat => cat.id === categoryId)[0]);
        });
}

/**
 * A display copy of a stored item: `{text, value}` plus `note` for a described "Other".
 *
 * @param {Object} item
 * @returns {{text: *, value: *, note?: string}}
 */
function displayItem(item) {
    const out = { text: item.text, value: item.value };
    const note = isOtherOption(item) ? itemOtherNote(item) : '';
    if (note !== '') out.note = note;
    return out;
}

/**
 * Group a stored value for the staff submission view and the PDF: one group per category that has
 * items (config order), then a trailing "Also on file" group for problems in no category. The None
 * sentinel collapses to a single titled group with no items. Empty/garbage → no groups at all.
 *
 * @param {Object} control
 * @param {*} value
 * @param {{noneLabel?: string, alsoOnFileLabel?: string}} [labels] - translated captions
 * @returns {Array<{id: string, title: string, items: Array<{text: *, value: *, note?: string}>}>}
 *   `note` is set only on a described "Other".
 */
function groupForDisplay(control, value, labels = {}) {
    if (!Array.isArray(value) || value.length === 0) return [];

    const captions = isPlainObject(labels) ? labels : {};
    const items = value.filter(isPlainObject);
    const real = items.filter(item => !isNoneOption(item));

    if (items.length > 0 && real.length === 0) {
        const noneLabel = captions.noneLabel
            || (isPlainObject(control) ? control.noneLabel : '')
            || DEFAULT_NONE_LABEL;
        return [{ id: NONE_GROUP_ID, title: noneLabel, items: [] }];
    }

    const normalized = controlCategories(control);
    const byCategory = {};
    const preserved = [];
    const preservedKeys = [];

    real.forEach(item => {
        const key = String(item.value);
        const categoryId = categoryIdForItem(normalized, item);
        if (categoryId === '') {
            if (preservedKeys.indexOf(key) !== -1) return;
            preservedKeys.push(key);
            preserved.push(displayItem(item));
            return;
        }
        if (!byCategory[categoryId]) byCategory[categoryId] = { keys: [], items: [] };
        if (byCategory[categoryId].keys.indexOf(key) !== -1) return;
        byCategory[categoryId].keys.push(key);
        byCategory[categoryId].items.push(displayItem(item));
    });

    const groups = [];
    normalized.forEach(cat => {
        const bucket = byCategory[cat.id];
        if (!bucket || bucket.items.length === 0) return;
        groups.push({ id: cat.id, title: cat.name, items: bucket.items });
    });

    if (preserved.length > 0) {
        groups.push({
            id: PRESERVED_GROUP_ID,
            title: captions.alsoOnFileLabel || ALSO_ON_FILE_LABEL,
            items: preserved
        });
    }

    return groups;
}

/**
 * Save gate for the Control Configuration sidebar. A categorized diseases control stores
 * DiseaseDefNums, so the only coherent mappings are the Diseases column or none at all. Every
 * other control — including the four legacy Open Dental data types — is left entirely alone.
 *
 * @param {Object} control
 * @returns {string} '' when the mapping is coherent, else the message to show.
 */
function getOdCategorizedMappingError(control) {
    if (!isCategorizedDiseases(control)) return '';
    const mapping = asText(control.teamioODColumn).trim();
    if (mapping === '' || mapping === 'Diseases') return '';
    return CATEGORIZED_MAPPING_ERROR;
}

/**
 * Trimmed `teamioODColumn` of a control ('' when none).
 *
 * @param {*} control
 * @returns {string}
 */
function odMapping(control) {
    return isPlainObject(control) ? asText(control.teamioODColumn).trim() : '';
}

/**
 * A control's label for a message: rich-text markup and ragged whitespace stripped, else its name.
 * Same rules as validate-od-unique-mapping.js controlDisplayName (both libraries stay import-free).
 *
 * @param {*} control
 * @returns {string}
 */
function controlLabel(control) {
    if (!isPlainObject(control)) return 'Untitled field';
    const label = asText(control.label).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    if (label !== '') return label;
    const name = asText(control.name).trim();
    return name !== '' ? name : 'Untitled field';
}

/**
 * A categorized diseases control that writes to Open Dental — the one that now asks for, and
 * saves, Other Problems itself.
 *
 * @param {*} control
 * @returns {boolean}
 */
function writesOtherProblemsItself(control) {
    return isCategorizedDiseases(control) && odMapping(control) === 'Diseases';
}

/**
 * Every control of a form, as an array (tolerates junk).
 *
 * @param {*} formData
 * @returns {Array<Object>}
 */
function formControls(formData) {
    if (!isPlainObject(formData) || !isPlainObject(formData.controls)) return [];
    return Object.keys(formData.controls).map(key => formData.controls[key]).filter(isPlainObject);
}

/**
 * Control Configuration sidebar gate: a categorized diseases control saved to Diseases already
 * asks for "Other" and writes Other Problems, so no other field on the form may also save to Other
 * Problems (two writers would concatenate into one note, and the legacy pairing would re-tick
 * "Other" behind the patient's back). Hidden controls count — the submit path walks them all.
 *
 * The control passed in is the sidebar's DEEP CLONE (carrying the mapping just picked); its own
 * entry in `formData.controls` still holds the OLD mapping, so it is skipped by `uniqueId`.
 *
 * @param {Object} control - the cloned control being configured
 * @param {Object} formData
 * @returns {string} '' when fine, else the reason the save is refused
 */
function getOdCategorizedOtherProblemsError(control, formData) {
    if (!isPlainObject(control)) return '';
    const others = formControls(formData).filter(other => other.uniqueId !== control.uniqueId);

    if (writesOtherProblemsItself(control)) {
        const conflict = others.filter(other => odMapping(other) === 'Other Problems')[0];
        if (conflict) {
            const name = controlLabel(conflict);
            return `This field asks patients to describe "Other" and saves it to Other Problems in Open Dental, so "${name}" cannot also save to Other Problems. Clear or change Save to Open Dental Field on "${name}" first.`;
        }
    }

    if (odMapping(control) === 'Other Problems') {
        const categorized = others.filter(writesOtherProblemsItself)[0];
        if (categorized) {
            return `"${controlLabel(categorized)}" already asks patients to describe "Other" and saves it to Other Problems in Open Dental, so no other field on this form can save to Other Problems. Clear or change Save to Open Dental Field on this field.`;
        }
    }

    return '';
}

/**
 * Form-level gate (create + edit screens, before saving): the same rule over the whole form.
 *
 * @param {Object} formData
 * @returns {string} '' when fine, else the reason the save is refused
 */
function getFormOdCategorizedOtherProblemsError(formData) {
    const controls = formControls(formData);
    const categorized = controls.filter(writesOtherProblemsItself)[0];
    if (!categorized) return '';
    const conflict = controls.filter(other => other !== categorized && odMapping(other) === 'Other Problems')[0];
    if (!conflict) return '';
    const name = controlLabel(conflict);
    return `Cannot save form: "${controlLabel(categorized)}" (DISEASES with Category and Checkbox Options) already asks patients to describe "Other" and saves it to Other Problems in Open Dental, so "${name}" cannot also save there. Clear or change Save to Open Dental Field on "${name}".`;
}

export {
    CATEGORIZED_DATA_TYPE,
    CATEGORIZED_DATA_TYPE_LABEL,
    DEFAULT_DISEASE_CATEGORIES,
    DEFAULT_NONE_LABEL,
    ALSO_ON_FILE_LABEL,
    NONE_GROUP_ID,
    PRESERVED_GROUP_ID,
    CATEGORIZED_MAPPING_ERROR,
    OTHER_DISEASE_NAME,
    OTHER_NOTE_KEY,
    DEFAULT_OTHER_CATEGORY,
    OTHER_NOTE_MAX_LENGTH,
    EXCLUDED_CATEGORY_RX,
    isNoneOption,
    isCategorizedDiseases,
    isOtherOption,
    isOtherOnlyCategory,
    requestDataTypeFor,
    dataTypeLabel,
    makeNoneItem,
    newCategoryId,
    normalizeCategories,
    placeOtherInDefaultCategory,
    seedDefaultCategories,
    restoreDefaultCategories,
    unionCategoryOptions,
    unassignedOptions,
    isExcludedCategoryName,
    assignableCategories,
    applyAssignments,
    findCategoryIdsForValue,
    emptyState,
    initStateFromValue,
    toggleCategoryOpen,
    setTick,
    setNoConditions,
    setOtherNote,
    otherNoteCategoryId,
    isOtherTicked,
    isOtherNoteRequired,
    syncOtherNote,
    projectState,
    getOtherNote,
    needsOtherNote,
    groupForDisplay,
    getOdCategorizedMappingError,
    getOdCategorizedOtherProblemsError,
    getFormOdCategorizedOtherProblemsError
};
